import React, { useEffect, useRef } from 'react';
import { Coordinates } from '../types';

interface LocationMapProps {
  currentLocation: Coordinates | null;
  selectedLocation: Coordinates | null;
  radius: number;
  isLoading: boolean;
  onLocationSelect: (location: Coordinates) => void;
  onMarkerDrag: (location: Coordinates) => void;
}

const LocationMap: React.FC<LocationMapProps> = ({
  currentLocation,
  selectedLocation,
  radius,
  isLoading,
  onLocationSelect,
  onMarkerDrag
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const currentLocationMarkerRef = useRef<any>(null);

  useEffect(() => {
    if (!currentLocation || !window.google?.maps) return;

    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    try {
      if (!mapRef.current) {
        const map = new google.maps.Map(mapElement, {
          center: currentLocation,
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapId: '8f52cca0c6d4e074',  // Valid Map ID for Advanced Markers
          gestureHandling: 'greedy'
        });

        mapRef.current = map;

        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          const latLng = e.latLng;
          if (latLng) {
            const clickedLocation = {
              lat: latLng.lat(),
              lng: latLng.lng()
            };
            onLocationSelect(clickedLocation);
            updateMarkers(clickedLocation, map);
          }
        });
      }

      updateCurrentLocationMarker(currentLocation);
      mapRef.current.setCenter(currentLocation);

    } catch (error) {
    }
  }, [currentLocation, onLocationSelect]);

  // Update markers when selected location or radius changes
  useEffect(() => {
    if (!mapRef.current || !selectedLocation) return;
    updateMarkers(selectedLocation, mapRef.current);
  }, [selectedLocation, radius]);

  const updateMarkers = (location: Coordinates, map: google.maps.Map) => {
    cleanupMarkers();

    // Create new marker
    const markerView = new google.maps.marker.PinElement({
      background: '#FF0000',
      borderColor: '#FFFFFF',
      glyphColor: '#FFFFFF',
      scale: 1.2
    });

    // Create or update the radius circle
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }
    
    circleRef.current = new google.maps.Circle({
      strokeColor: '#3498db',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3498db',
      fillOpacity: 0.15,
      map: map,
      center: location,
      radius: radius,
      clickable: false,
      zIndex: 1
    });

    // Adjust map bounds to show the entire circle
    const bounds = circleRef.current.getBounds();
    if (bounds) {
      map.fitBounds(bounds);
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: location,
      map: map,
      title: 'Work Location',
      content: markerView.element,
      gmpDraggable: true
    });

    markerRef.current = marker;

    marker.addEventListener('dragend', () => {
      const newPos = marker.position;
      if (newPos) {
        const draggedLocation = {
          lat: typeof newPos.lat === 'function' ? newPos.lat() : newPos.lat,
          lng: typeof newPos.lng === 'function' ? newPos.lng() : newPos.lng
        };
        onMarkerDrag(draggedLocation);
        if (circleRef.current) {
          circleRef.current.setCenter(newPos);
        }
      }
    });
  };

  const updateCurrentLocationMarker = (location: Coordinates) => {
    if (!mapRef.current) return;

    if (currentLocationMarkerRef.current) {
      currentLocationMarkerRef.current.map = null;
    }

    const markerView = new google.maps.marker.PinElement({
      background: '#4285F4',
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      scale: 1.2
    });

    currentLocationMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef.current,
      position: location,
      title: 'Current Location',
      content: markerView.element
    });
  };

  const cleanupMarkers = () => {
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMarkers();
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.map = null;
      }
    };
  }, []);

  return (
    <div className="map-container">
      <div id="map" style={{ width: '100%', height: '400px' }}>
        {isLoading && (
          <div className="map-loading">
            <div className="loading-spinner"></div>
            <p>Loading map...</p>
          </div>
        )}
      </div>
      {selectedLocation && (
        <div className="selected-location-info">
          <p>Selected Location: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}</p>
        </div>
      )}
    </div>
  );
};

export default LocationMap;
